import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
// @mui
import { alpha, ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
// hooks
import useSettings from '../../hooks/useSettings';
//
import componentsOverride from '../../theme/overrides';

// ----------------------------------------------------------------------

ThemeColorPresets.propTypes = {
  children: PropTypes.node,
};

export default function ThemeColorPresets({ children }) {
  const defaultTheme = useTheme();

  const { setColor } = useSettings();

  const themeOptions = useMemo(
    () => ({
      ...defaultTheme,
      palette: {
        ...defaultTheme.palette,
        primary: setColor,
      },
      customShadows: {
        ...defaultTheme.customShadows,
        primary: `0 8px 16px 0 ${alpha(setColor.main, 0.24)}`,
      },
    }),
    [setColor, defaultTheme]
  );

  const theme = createTheme(themeOptions);

  const {
    lighter,
    light,
    main,
    dark,
    darker,
    gradient1,
    gradient2,
    gradient3,
    gradient4,
    gradient5,
    gradient6,
    gradientIOS1,
    gradientIOS2,
    gradientIOS3,
    gradientIOS4,
    gradientIOS5,
    gradientIOS6,
  } = theme.palette.primary;

  const dynamicGradient = `linear-gradient(227.6deg, ${lighter} 2.08%, ${light} 50%, ${dark} 97.92%)`;
  const dynamicGradient2 = `linear-gradient(180deg, ${lighter} 0%, ${light} 50%, ${dark} 97.92%)`;
  // Multi-layer wash matching the parent project: white overlay on top-right + saturated
  // brand-color overlay on top-left + vertical fade. Each layer is capped at `gradientHeight`
  // via `background-size` so the wash stops around the deposit/transfer/withdraw panel
  // regardless of page length. `gradientIOS6` (page base) fills everything below.
  const gradientHeight = '500px';
  const dynamicGradient3 = [
    `radial-gradient(60% 45% at 90% 10%, ${gradientIOS1} 0%, transparent 59%) 0 0 / 100% ${gradientHeight} no-repeat`,
    `radial-gradient(80% 60% at 20% 0%, ${gradientIOS2} 0%, transparent 80%) 0 0 / 100% ${gradientHeight} no-repeat`,
    `linear-gradient(180deg, ${gradientIOS3} 0%, ${gradientIOS4} 15%, ${gradientIOS5} 35%, ${gradientIOS6} 100%) 0 0 / 100% ${gradientHeight} no-repeat`,
    `${gradientIOS6}`,
  ].join(', ');
  const dynamicGradient3Ios = dynamicGradient3;
  const dynamicButtonPrimary = `linear-gradient(90deg, ${dark} 0%, ${main} 50%, ${light} 100%)`;
  const dynamicDepositBg = `linear-gradient(180deg, ${lighter} 0%, ${main} 50%, ${dark} 100%)`;

  // Override gradient inside palette
  if (theme.palette.gradients) {
    theme.palette.gradients.theme = dynamicGradient;
    theme.palette.gradients.theme2 = dynamicGradient2;
    theme.palette.gradients.theme3 = dynamicGradient3;
    theme.palette.gradients.theme3Ios = dynamicGradient3Ios;
    theme.palette.gradients.buttonPrimary = dynamicButtonPrimary;
  }

  // Shortcuts used by buttons across the app
  if (theme.palette.button) {
    theme.palette.button.main = main;
    theme.palette.button.secondary = light;
    theme.palette.button.hover = dark;
  }

  // Header
  if (theme.palette.header) {
    theme.palette.header.bgColor = main;
    theme.palette.header.menuBg = dark;
    theme.palette.header.menu = darker;
    if (theme.palette.header.deposit_btn) theme.palette.header.deposit_btn.bg = dynamicButtonPrimary;
    if (theme.palette.header.register_btn) theme.palette.header.register_btn.bg = dynamicGradient;
    if (theme.palette.header.login_btn) {
      theme.palette.header.login_btn.border = dark;
      theme.palette.header.login_btn.color = dark;
    }
    if (theme.palette.header.balance_button) theme.palette.header.balance_button.bg = dark;
  }

  // Side nav
  if (theme.palette.navBarVertical) {
    theme.palette.navBarVertical.bg = main;
  }

  // Bottom menu (mobile)
  if (theme.palette.bottomMenu) {
    theme.palette.bottomMenu.color = dark;
    theme.palette.bottomMenu.depositBg = dynamicDepositBg;
    theme.palette.bottomMenu.depositBorder = dark;
  }

  // Home accents
  if (theme.palette.home) {
    if (theme.palette.home.announcement) {
      theme.palette.home.announcement.bg = darker;
      theme.palette.home.announcement.color = dark;
    }
    if (theme.palette.home.tab) {
      theme.palette.home.tab.borderBottom = dark;
      theme.palette.home.tab.bgActive = dark;
    }
    if (theme.palette.home.sort) {
      theme.palette.home.sort.labelColor = dark;
    }
  }

  // Footer
  if (theme.palette.footer) {
    theme.palette.footer.bgColor = darker;
  }

  // Visitor (login/register)
  if (theme.palette.visitor) {
    theme.palette.visitor.link = dark;
    if (theme.palette.visitor.login) {
      theme.palette.visitor.login.buttonBg = dynamicGradient;
      theme.palette.visitor.login.border = dark;
    }
    if (theme.palette.visitor.register) {
      theme.palette.visitor.register.buttonBg = dynamicGradient;
      theme.palette.visitor.register.border = dark;
    }
  }

  // Deposit / withdraw dialog
  if (theme.palette.transactionDialog) {
    theme.palette.transactionDialog.border = dark;
    if (theme.palette.transactionDialog.deposit) {
      theme.palette.transactionDialog.deposit.confirmBtn = dynamicGradient;
    }
  }

  theme.components = componentsOverride(theme);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
